import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import authService from "./authService";
import {
  EditMeType,
  EditUserType,
  LoginData,
  LoginResponse,
  RegisterDataType,
  ResetPasswordTypes,
  RoleEditType,
  SuccessType,
  UserWithRoles,
  changePasswordType,
  recoverPasswordData,
} from "./types";
import { setCookie, deleteCookie, TOKEN_COOKIE } from "../../utils/cookies";

interface AuthState {
  user: UserWithRoles | null;
  loading: boolean;
  error: boolean;
  message: string | null;
  loginError: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  message: null,
  loginError: null,
  error: false,
};

export const login = createAsyncThunk<
  LoginResponse,
  LoginData,
  { rejectValue: string }
>("auth/loginaut", async (data: LoginData, { rejectWithValue }) => {
  try {
    const response: LoginResponse = await authService.login(data);
    return response;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(
        error.response.data.message || "Error desconocido al iniciar sesión.",
      );
    }
    return rejectWithValue(
      error.message || "Error desconocido al iniciar sesión.",
    );
  }
});

export const newUser = createAsyncThunk<
  SuccessType,
  { data: RegisterDataType; publicationSlug: string },
  { rejectValue: string }
>("auth/newUser", async ({ data, publicationSlug }, { rejectWithValue }) => {
  try {
    const response = await authService.newUser(data, publicationSlug);
    return response;
  } catch (error: any) {
    if (typeof error === "string") {
      return rejectWithValue(error);
    } else if (error.message) {
      return rejectWithValue(error);
    } else if (error.response?.data?.detail) {
      return rejectWithValue(error.response.data.detail);
    } else {
      return rejectWithValue("Error creating user");
    }
  }
});

export const checkUserAlreadyExists = createAsyncThunk<
  boolean,
  string,
  { rejectValue: string }
>(
  "auth/checkUserAlreadyExists",
  async (user_login: string, { rejectWithValue }) => {
    try {
      const res = await authService.checkUserAlreadyExists(user_login);
      return res;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getMe = createAsyncThunk<
  UserWithRoles,
  void,
  { rejectValue: string }
>("auth/getMe", async (_, { rejectWithValue }) => {
  try {
    const response = await authService.getMe();
    return response;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data || "Error al obtener el usuario conectado.",
    );
  }
});

export const recoverPassword = createAsyncThunk<
  string,
  recoverPasswordData,
  { rejectValue: string }
>(
  "auth/recoverPassword",
  async (data: recoverPasswordData, { rejectWithValue }) => {
    try {
      const response = await authService.recoverPassword(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const resetPassword = createAsyncThunk<
  SuccessType,
  ResetPasswordTypes,
  { rejectValue: string }
>(
  "auth/resetPassword",
  async (data: ResetPasswordTypes, { rejectWithValue }) => {
    try {
      const response = await authService.resetPassword(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const editUser = createAsyncThunk<
  SuccessType,
  { user_login: string; data: EditUserType },
  { rejectValue: string }
>(
  "auth/editUser",
  async (
    { user_login, data }: { user_login: string; data: EditUserType },
    { rejectWithValue },
  ) => {
    try {
      const response = await authService.editUser({ user_login, data });
      return response;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message || "Error desconocido");
    }
  },
);

export const editMe = createAsyncThunk<
  SuccessType,
  EditMeType,
  { rejectValue: string }
>("auth/editMe", async (data: EditMeType, { rejectWithValue }) => {
  try {
    const response = await authService.editMe(data);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const editMyRol = createAsyncThunk<
  void,
  RoleEditType,
  { rejectValue: string }
>("auth/editMyRol", async (data: RoleEditType, { rejectWithValue }) => {
  try {
    await authService.editMyRol(data);
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const changePassword = createAsyncThunk<
  SuccessType,
  changePasswordType,
  { rejectValue: string }
>(
  "auth/changePassword",
  async (data: changePasswordType, { rejectWithValue }) => {
    try {
      const response = await authService.changePassword(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const addProfilePicture = createAsyncThunk<
  SuccessType,
  FormData,
  { rejectValue: string }
>("auth/addProfilePicture", async (data: FormData, { rejectWithValue }) => {
  try {
    const response = await authService.addProfilePicture(data);
    return response;
  } catch (error: any) {
    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      "No se pudo subir la imagen de perfil.";

    return rejectWithValue(message);
  }
});

export const deleteUser = createAsyncThunk<
  SuccessType,
  string,
  { rejectValue: string }
>("auth/deleteUser", async (user_login: string, { rejectWithValue }) => {
  try {
    const response = await authService.deleteUser(user_login);
    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const logout = createAsyncThunk("auth/logout", async () => {
  authService.logout();
  localStorage.removeItem("token");
  deleteCookie(TOKEN_COOKIE);
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearErrors(state) {
      state.message = null;
      state.loginError = null;
    },
    resetAuthState(state) {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.loginError = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        const days = action.meta.arg.remember ? 365 : 30;
        setCookie(TOKEN_COOKIE, action.payload.access_token, days);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.loginError = action.payload ?? "Error de login";
      })

      .addCase(getMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getMe.rejected, (state) => {
        state.loading = false;
        state.user = null;
      })

      .addCase(newUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(newUser.fulfilled, (state) => {
        state.loading = false;
        state.message = "User created successfully";
      })
      .addCase(newUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error creating user";
      })

      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.loading = false;
        state.message = "Usuario eliminado correctamente";
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error eliminando usuario";
      })

      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.loading = false;
      })

      .addCase(editUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(editUser.fulfilled, (state) => {
        state.loading = false;
        state.message = "Usuario editado correctamente";
      })
      .addCase(editUser.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error editando usuario";
      })

      .addCase(editMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(editMe.fulfilled, (state) => {
        state.loading = false;
        state.message = "Perfil actualizado correctamente";
      })
      .addCase(editMe.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error actualizando perfil";
      })

      .addCase(changePassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.message = "Contraseña cambiada correctamente";
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error cambiando contraseña";
      })

      .addCase(recoverPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(recoverPassword.fulfilled, (state) => {
        state.loading = false;
        state.message = "Correo de recuperación enviado";
      })
      .addCase(recoverPassword.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error en recuperación";
      })

      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.message = "Contraseña restablecida correctamente";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error restableciendo contraseña";
      })

      .addCase(addProfilePicture.pending, (state) => {
        state.loading = true;
      })
      .addCase(addProfilePicture.fulfilled, (state) => {
        state.loading = false;
        state.message = "Foto de perfil actualizada";
      })
      .addCase(addProfilePicture.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error actualizando foto";
      })

      .addCase(checkUserAlreadyExists.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkUserAlreadyExists.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkUserAlreadyExists.rejected, (state) => {
        state.loading = false;
      })

      .addCase(editMyRol.pending, (state) => {
        state.loading = true;
      })
      .addCase(editMyRol.fulfilled, (state) => {
        state.loading = false;
        state.message = "Rol actualizado correctamente";
      })
      .addCase(editMyRol.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload ?? "Error actualizando rol";
      });
  },
});

export const { clearErrors, resetAuthState } = authSlice.actions;
export default authSlice.reducer;
